import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { useMemo } from "react";
import { removeUnderScoreWithUpperCase } from "../../../utils/removeUnderscore";
import { SYSTEM_USER_ID } from "../../../utils/constants";
import { useGetAllSuppliers, useGetGrazeons } from "../queryHooks";
import { useOrdersStore } from "../store/useOrdersStore";
import {
  formatDiffFromPrevious,
  LifecycleStep,
  toMillis,
} from "../../ControlTower/buildLifecycleSteps";
import { formatLifecycleCell } from "../../ControlTower/formatLifecycleCell";
import LifecycleStepsTable from "../../ControlTower/LifecycleStepsTable";

export type OrderTimelineEntry = {
  scope?: string;
  from?: string | null;
  to?: string;
  userId?: string;
  supplierId?: string;
  at?: string | Date;
};

type AuditTrailModalProps = {
  isOpen: boolean;
  onClose: () => void;
  orderTimeline?: OrderTimelineEntry[] | null;
};

const formatStatus = (status?: string | null) => {
  if (!status) return null;
  const leaf = status.includes(".") ? status.split(".").pop() : status;
  return removeUnderScoreWithUpperCase(leaf ?? status);
};

/** State that was left at this event (`from`); fall back to `to` for the first entry. */
const formatLeavingState = (entry: OrderTimelineEntry) => {
  return formatStatus(entry.from) ?? formatStatus(entry.to) ?? "—";
};

const idKey = (id?: string | null) => (id ? String(id) : "");

const AuditTrailModal = ({
  isOpen,
  onClose,
  orderTimeline,
}: AuditTrailModalProps) => {
  const { data: allSuppliers } = useGetAllSuppliers();
  const { data: grazeons } = useGetGrazeons();
  const orderHandlers = useOrdersStore((state) => state.orderHandlers);

  const userNameById = useMemo(() => {
    const map: Record<string, string> = {};

    orderHandlers?.forEach((handler: any) => {
      const user = handler?.user;
      const key = idKey(user?._id || handler?.userId || handler?._id);
      if (!key) return;
      const name = [user?.firstName, user?.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
      if (name) map[key] = name;
    });

    const grazeonList = Array.isArray(grazeons?.data)
      ? grazeons.data
      : Array.isArray(grazeons)
        ? grazeons
        : [];
    grazeonList.forEach((grazeon: any) => {
      const user = grazeon?.user;
      const key = idKey(user?._id || grazeon?.userId);
      if (!key || map[key]) return;
      const name = [user?.firstName, user?.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
      if (name) map[key] = name;
    });

    return map;
  }, [orderHandlers, grazeons]);

  const supplierNameById = useMemo(() => {
    const map: Record<string, string> = {};
    (allSuppliers || []).forEach((supplier: any) => {
      const key = idKey(supplier?._id);
      if (key && supplier?.businessName) {
        map[key] = supplier.businessName;
      }
    });
    return map;
  }, [allSuppliers]);

  const resolvePerformedBy = (entry: OrderTimelineEntry) => {
    const userId = idKey(entry.userId);
    const supplierName = supplierNameById[idKey(entry.supplierId)];
    const userName =
      userId === SYSTEM_USER_ID
        ? "SYSTEM"
        : userNameById[userId];

    if (userName && supplierName) {
      return `${userName} · ${supplierName}`;
    }
    if (userName) return userName;
    if (supplierName) return supplierName;
    if (entry.userId || entry.supplierId) return "Unknown";
    return "—";
  };

  const steps: LifecycleStep[] = useMemo(() => {
    if (!Array.isArray(orderTimeline)) return [];

    const sorted = [...orderTimeline].sort((a, b) => {
      const aTime = a?.at ? new Date(a.at).getTime() : 0;
      const bTime = b?.at ? new Date(b.at).getTime() : 0;
      return aTime - bTime;
    });

    let previousMs = 0;
    return sorted.map((entry, idx) => {
      const currentMs = toMillis(entry.at ?? null);
      const atRaw = (() => {
        if (entry.at instanceof Date) {
          const ms = entry.at.getTime();
          return Number.isFinite(ms) ? entry.at.toISOString() : null;
        }
        if (entry.at != null) return String(entry.at);
        return null;
      })();
      const diffFromPrevious =
        idx === 0 || previousMs <= 0 || currentMs <= 0
          ? "0"
          : formatDiffFromPrevious(previousMs, currentMs);
      if (currentMs > 0) previousMs = currentMs;

      return {
        id: `${entry.scope}-${entry.from}-${entry.to}-${atRaw}-${idx}`,
        status: formatLeavingState(entry),
        at: formatLifecycleCell(entry.at ?? atRaw),
        atRaw,
        person: resolvePerformedBy(entry),
        diffFromPrevious,
      };
    });
    // resolvePerformedBy depends on name maps; include those deps explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderTimeline, userNameById, supplierNameById]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      scrollBehavior="inside"
      returnFocusOnClose={false}
    >
      <ModalOverlay />
      <ModalContent maxH="85vh">
        <ModalHeader py={3} fontSize="md">
          Audit Trail
          <ModalCloseButton />
        </ModalHeader>
        <ModalBody py={2} overflowY="auto">
          <LifecycleStepsTable
            steps={steps}
            emptyMessage="No audit trail events available."
          />
        </ModalBody>
        <ModalFooter py={2}>
          <Button colorScheme="blue" size="sm" onClick={onClose}>
            OK
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AuditTrailModal;
